import type * as openapi from '@/helpers/api/types/openapiOverrides';

import {
  ARServicesDeliveryList,
  ARServicesPickupList,
  zammadTicketRfcStatuses,
} from '@/utils/PortalConstants';
import { PageContext } from '@/utils/zulip/types';
import { ARServiceItem } from '@/models/airRoad';
import {
  JobTicketDashboardItem,
  JobTicketListItem,
  JobTicketResponce,
} from '@/models/jobTicket';
import { SchedulerUnscheduledStop } from '@/models/scheduler';

export function sortByValue(
  array: SchedulerUnscheduledStop[] | null,
  value: string,
): SchedulerUnscheduledStop[] {
  if (array) {
    const sortedArray = array.sort((a, b) => {
      // TODO
      // @ts-expect-error
      if (a[value] < b[value]) {
        return -1;
      }
      // TODO
      // @ts-expect-error
      if (a[value] > b[value]) {
        return 1;
      }
      return 0;
    });
    return sortedArray;
  } else {
    return [];
  }
}

export function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function checkTicketIsRfc(
  ticket: JobTicketListItem | JobTicketResponce | JobTicketDashboardItem,
): boolean {
  if (
    // TODO
    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */
    !!ticket.is_rfc &&
    ticket.state &&
    zammadTicketRfcStatuses.includes(ticket.state.toUpperCase())
  ) {
    return true;
  }
  return false;
}

export function isNumeric(value: string): boolean {
  return /^-?\d+$/.test(value);
}

export function validateEmail(email: string): boolean {
  const regex = /^([\w-\\.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return regex.test(email);
}

export function validateEmails(emails: string): boolean {
  const result = emails.replace(/[ ,]+/g, ',').replace(/\s/g, '').split(/,|;/);
  /* eslint-disable-next-line @typescript-eslint/prefer-for-of */
  for (let i = 0; i < result.length; i++) {
    if (!validateEmail(result[i])) {
      return false;
    }
  }
  return true;
}

export function generateUserEmailName(
  user: openapi.UserProfileReadContentOpenapi,
): string {
  let userName = '';
  if (user.first_name) {
    userName = user.first_name;
  }
  if (user.last_name) {
    userName += ' ' + user.last_name;
  }
  if (!user.first_name && !user.last_name && user.username) {
    userName = user.username;
  }
  return userName;
}

export function extractEmails(text: string): null | string[] {
  return text.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi);
}

export function cleanEmailString(
  emailsToFilter: string[],
  emailsToDelete: string[],
): string[] {
  const toSet = new Set(emailsToDelete);
  const toFiltred = [...new Set(emailsToFilter)].filter((email) => {
    return !toSet.has(email);
  });
  return toFiltred;
}

export function getARServiceData(id: number): ARServiceItem | null {
  const ARServicesList = { ...ARServicesDeliveryList, ...ARServicesPickupList };
  const option = Object.values(ARServicesList).find(
    (x) => x.specialOptionId === id,
  );
  if (option) {
    return option;
  }
  return null;
}

/**
 * Get page context data. Used to send along with messages to Zulip.
 */
export function getPageContext(): PageContext {
  return {
    url: window.location.href,
    title: document.title,
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    screenSize: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

/**
 * Extract image path from full URL for API deletion. Handles paths like:
 * - https://example.com/dashtest-images/rhApp/...
 * - https://example.com/images/rhApp/...
 * - https://example.com/rhApp/...
 */
export function extractImagePathForDeletion(fullPath: string): string {
  const pathWithoutQuery = fullPath.split('?')[0];

  // Dashtest: dashtest-images/rhApp/...
  const dashtestRegex = /(dashtest-images\/rhApp\/.+)$/;
  const dashtestMatch = dashtestRegex.exec(pathWithoutQuery);
  if (dashtestMatch) {
    return decodeURIComponent(dashtestMatch[1]);
  }

  // Production: images/rhApp/... -> extract rhApp/...
  const imagesRegex = /images\/(rhApp\/.+)$/;
  const imagesMatch = imagesRegex.exec(pathWithoutQuery);
  if (imagesMatch) {
    return decodeURIComponent(imagesMatch[1]);
  }

  // Production: rhApp/...
  const rhAppRegex = /(rhApp\/.+)$/;
  const rhAppMatch = rhAppRegex.exec(pathWithoutQuery);
  if (rhAppMatch) {
    return decodeURIComponent(rhAppMatch[1]);
  }

  // Fallback for paths that already start with the expected prefix
  if (
    pathWithoutQuery.startsWith('dashtest-images/') ||
    pathWithoutQuery.startsWith('images/') ||
    pathWithoutQuery.startsWith('rhApp/')
  ) {
    return decodeURIComponent(pathWithoutQuery);
  }

  return fullPath;
}
